import { FC, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import FormButtons from '../../../../app/components/FormButtons';
import { MealItemCustomizationRules } from '../../../../app/data/models/mealItems/mealItemCustomization';
import { useAppDispatch } from '../../../../app/store/configureStore';
import { updateMealItemCustomizationRulesAsync } from './updateMealItemCustomizationSlice';
import ListTitleBox from '../../../../app/components/list/ListTitleBox';

interface Props {
  restaurantId: string;
  customizationId: string;
  rules?: MealItemCustomizationRules;
  onClose: (success: boolean) => void;
}

const MealItemCustomizationRuleForm: FC<Props> = ({
  restaurantId,
  customizationId,
  rules,
  onClose,
}) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { control, handleSubmit, watch } = useForm<MealItemCustomizationRules>({
    resolver: yupResolver(
      yup.object().shape({
        minimumQuantity: yup
          .number()
          .required(t('customization-rules.validation.required'))
          .min(
            0,
            t('customization-rules.validation.min-quantity', { value: 0 })
          ),
        maximumQuantity: yup
          .number()
          .required(t('customization-rules.validation.required'))
          .min(
            yup.ref('minimumQuantity'),
            t('customization-rules.validation.max-greater-than-min')
          ),
        multipleSelection: yup.boolean().required(),
        maxSelectionsPerOption: yup.number().when('multipleSelection', {
          is: true,
          then: (schema) =>
            schema
              .required(t('customization-rules.validation.required'))
              .min(
                1,
                t('customization-rules.validation.min-selections', { value: 1 })
              ),
        }),
      })
    ),
    defaultValues: {
      minimumQuantity: rules?.minimumQuantity ?? 0,
      maximumQuantity: rules?.maximumQuantity ?? 1,
      multipleSelection: rules?.multipleSelection ?? false,
      maxSelectionsPerOption: rules?.maxSelectionsPerOption ?? 1,
    },
  });

  const multipleSelection = watch('multipleSelection');

  const dispatch = useAppDispatch();

  const onSubmit = async (data: MealItemCustomizationRules) => {
    setIsSubmitting(true);
    try {
      await dispatch(
        updateMealItemCustomizationRulesAsync({
          restaurantId,
          customizationId,
          data,
        })
      );
      onClose(true);
    } catch (error) {
      console.error('Failed to update rules:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const header = (
    <ListTitleBox
      title={t('customization-rules.title')}
      onBackClick={() => onClose(false)}
    />
  );

  return (
    <>
      {header}
      <Box component={Paper} sx={{ width: '100%', mb: 3, p: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 3 }}>
          {t('customization-rules.description')}
        </Typography>

        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Controller
              name="minimumQuantity"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    {t('customization-rules.minimum-quantity')}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    {t('customization-rules.minimum-quantity-description')}
                  </Typography>
                  <TextField
                    {...field}
                    type="number"
                    error={!!error}
                    helperText={error?.message}
                    fullWidth
                    inputProps={{ min: 0 }}
                  />
                </Box>
              )}
            />

            <Controller
              name="maximumQuantity"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    {t('customization-rules.maximum-quantity')}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    {t('customization-rules.maximum-quantity-description')}
                  </Typography>
                  <TextField
                    {...field}
                    type="number"
                    error={!!error}
                    helperText={error?.message}
                    fullWidth
                    inputProps={{ min: 0 }}
                  />
                </Box>
              )}
            />

            <Controller
              name="multipleSelection"
              control={control}
              defaultValue={false}
              render={({ field: { value, onChange } }) => (
                <Box>
                  <Typography variant="subtitle1" gutterBottom>
                    {t('customization-rules.multiple-selections')}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    {t('customization-rules.multiple-selections-description')}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={value}
                        onChange={(e) => onChange(e.target.checked)}
                      />
                    }
                    label={t('customization-rules.allow-multiple')}
                  />
                </Box>
              )}
            />

            {multipleSelection && (
              <Controller
                name="maxSelectionsPerOption"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      {t('customization-rules.max-selections-per-option')}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {t(
                        'customization-rules.max-selections-per-option-description'
                      )}
                    </Typography>
                    <TextField
                      {...field}
                      type="number"
                      error={!!error}
                      helperText={error?.message}
                      fullWidth
                      inputProps={{ min: 1 }}
                    />
                  </Box>
                )}
              />
            )}
          </Box>

          <Box sx={{ mt: 4 }}>
            <FormButtons loading={isSubmitting} submitText={t('common.save')} />
          </Box>
        </form>
      </Box>
    </>
  );
};

export default MealItemCustomizationRuleForm;
